import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://klap.app/pricing";
const MONTHLY_SOURCE = "https://klap.app/blog/opus-clip-pricing";
const CHECKED = "2026-10-08";
const VERIFIED = "2026-10-09";
const MONTHLY_NOTE = "Monthly and yearly figures read from the pricing page toggles in a real browser on Oct 9, 2026.";

export const KLAP: CompetitorRecord = {
  slug: "klap",
  name: "Klap",
  aliases: ["Klap AI", "Klap app", "klap.app"],
  url: "https://klap.app",
  pricingUrl: PRICING,
  category: "clipper",
  positioning: "Turn videos into viral TikToks, Reels and Shorts.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "1 video",
      includes: [
        "No credit card required",
        "1 video to try the clipper",
        "Watermark on exports, per a third-party review",
      ],
      sourceUrl: "https://klap.app/",
      checkedAt: CHECKED,
      note: "The watermark is reported by toolsforhumans.ai (Mar 24, 2026), not stated on Klap's pricing page.",
    },
    {
      name: "Basic",
      monthly: 29,
      annualPerMonth: 14,
      metered: "100 clips per month, 1 clip per clip generated",
      includes: [
        "10 videos per month, up to 45 minutes each",
        "HD downloads",
        "Unlimited social accounts (TikTok, Instagram, LinkedIn)",
        "Analytics",
        "API access",
      ],
      sourceUrl: PRICING,
      checkedAt: VERIFIED,
      note: MONTHLY_NOTE,
    },
    {
      name: "Pro",
      monthly: 79,
      annualPerMonth: 39,
      metered: "300 clips per month, 1 clip per clip generated",
      includes: [
        "30 videos per month, up to 2 hours each",
        "4K downloads",
        "AI dubbing in 29 languages",
        "Unlimited social accounts and analytics",
        "API access",
      ],
      sourceUrl: PRICING,
      checkedAt: VERIFIED,
      note: MONTHLY_NOTE,
    },
    {
      name: "Pro+",
      monthly: 189,
      annualPerMonth: 94,
      metered: "1,000 clips per month, 1 clip per clip generated",
      includes: [
        "100 videos per month, up to 3 hours each",
        "4K downloads and AI dubbing",
        "Labelled For teams, seat count not stated",
        "Unlimited social accounts and analytics",
        "API access",
      ],
      sourceUrl: PRICING,
      checkedAt: VERIFIED,
      note: MONTHLY_NOTE,
    },
  ],
  entryPlanName: "Basic",
  entryPlanIncludes: "100 clips a month from 10 videos of up to 45 minutes each, HD downloads",
  freeTier: "1 video, no credit card, watermarked per a third-party review",
  trial: "None advertised. The free plan is the try-before-you-buy path.",
  meteringUnit: "1 clip per clip generated, with a separate cap on videos per month and minutes per video",
  exportResolution: "HD on Basic, 4K on Pro and Pro+",
  features: {
    longToShort: { v: "yes", note: "About 5 clips per minute of source" },
    momentRanking: { v: "yes", note: "Scores each clip's viral potential" },
    reframing: { v: "yes", note: "AI Reframe 2" },
    maxSource: { v: "text", note: "45 min, 2 h or 3 h by plan" },
    layouts: { v: "text", note: "Split screen, screencasts, gaming via AI Reframe 2" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no", note: "Not advertised" },
    voiceover: { v: "partial", note: "AI dubbing in 29 languages, Pro and up" },
    voiceCloning: { v: "n/a", note: "Not advertised" },
    avatars: { v: "n/a", note: "Not advertised" },
    timelineEditor: { v: "yes", note: "Klap says a full editor on every plan" },
    captionStyles: { v: "n/a", note: "Count not published" },
    captionLanguages: { v: "text", note: "52" },
    bRoll: { v: "partial", note: "Listed only on their Opus Clip page" },
    fourK: { v: "partial", note: "Pro and Pro+ only" },
    scheduling: { v: "yes", note: "TikTok, YouTube, Instagram, LinkedIn" },
    analytics: { v: "yes", note: "On every plan" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "Every plan, with managed sub-users" },
    webhooks: { v: "n/a", note: "Not stated on the API page" },
    web: { v: "yes" },
    desktop: { v: "no" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "n/a", note: "Pro+ says For teams, no seat count" },
    freeTier: { v: "yes", note: "1 video, watermarked" },
    trial: { v: "text", note: "Not stated; free plan instead" },
    metering: { v: "text", note: "1 clip per clip generated, plus video caps" },
    rollover: { v: "n/a", note: "Not stated on the pricing page" },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "NO option to cancel or change anything",
      date: "2026-06-10",
      url: "https://www.trustpilot.com/review/klap.app",
      evidences: "cancelInApp",
    },
    {
      text: "it is a bit expensive for that type of service",
      date: "2026-09-15",
      url: "https://www.trustpilot.com/review/klap.app",
      evidences: "metering",
    },
  ],
  theirCrayoPages: [
    {
      url: "https://klap.app/blog/crayo-ai-alternatives",
      date: "2025-04-30",
      claims: [
        "Crayo.ai currently does not offer a free plan",
        "Crayo.ai's tools do not work as advertised (laggy exports, pixelated output, subtitle issues)",
        "The customer support system, primarily routed through Discord, is ineffective",
        "Voices are robotic and lacking polish",
        "Quotes no Crayo prices",
      ],
    },
  ],
  verification: {
    method: "browser",
    date: CHECKED,
    note: `Read in a real browser on Oct 9, 2026 on both toggles of ${PRICING}: yearly $14, $39 and $94 a month billed yearly; monthly $29, $79 and $189. The same monthly figures appear in Klap's blog post ${MONTHLY_SOURCE} dated Jul 21, 2026. Annual totals are not published and are omitted. The free-plan watermark is a third-party claim from toolsforhumans.ai.`,
  },
  seoPattern:
    "klap.app/alternatives/<competitor> (20 pages) plus klap.app/blog/<competitor>-alternatives; one blog post about Crayo",
};
